import io.github.jan.supabase.auth.Auth
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.providers.builtin.Email
import io.github.jan.supabase.createSupabaseClient
import io.github.jan.supabase.postgrest.Postgrest
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import io.github.jan.supabase.serializer.KotlinXSerializer
import kotlinx.serialization.json.Json

val supabase by lazy {
    createSupabaseClient(
        supabaseUrl = "https://uexhciebwifavjdjecex.supabase.co",
        supabaseKey = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InVleGhjaWVid2lmYXZqZGplY2V4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc4Nzk3NzAsImV4cCI6MjA4MzQ1NTc3MH0.1lLW6FmFCLAMz-T10GX7ce5gUWddtYqBAjUAKyobkQ0"
    ) {
        install(Postgrest) {
            serializer = KotlinXSerializer(Json {
                ignoreUnknownKeys = true
                isLenient = true
            })
        }

        install(Auth)
    }
}

object DataRepository {

    // --- АВТОРИЗАЦИЯ ---

    suspend fun login(email: String, pass: String): Boolean {
        return try {
            supabase.auth.signInWith(Email) {
                this.email = email
                this.password = pass
            }
            true
        } catch (e: Exception) {
            console.log("Login error: ${e.message}")
            false
        }
    }

    suspend fun register(email: String, pass: String): Boolean {
        return try {
            supabase.auth.signUpWith(Email) {
                this.email = email
                this.password = pass
            }
            true
        } catch (e: Exception) {
            console.log("Register error: ${e.message}")
            false
        }
    }

    suspend fun logout() {
        try {
            supabase.auth.signOut()
        } catch (e: Exception) {
        }
    }

    // --- ПРОГРЕСС ---

    // Функция сохранения прогресса (с учетом set_id)
    suspend fun saveProgress(taskId: Int, setId: String, score: Int, total: Int) {
        try {
            val userId = supabase.auth.currentUserOrNull()?.id ?: return

            // ИСПОЛЬЗУЕМ КЛАСС ВМЕСТО mapOf
            val dataToSave = ProgressUploadDto(
                userId = userId,
                taskId = taskId,
                setId = setId,
                score = score,
                totalQuestions = total,
                updatedAt = kotlinx.datetime.Clock.System.now().toString()
            )

            // Передаем объект. onConflict работает по user_id и set_id
            supabase.from("user_progress").upsert(dataToSave) {
                onConflict = "user_id, set_id"
            }

            console.log(">>> Прогресс успешно сохранен!")

        } catch (e: Exception) {
            console.error("Save error: ${e.message}")
        }
    }

    suspend fun getMyProgress(): List<UserProgress> {
        return try {
            if (supabase.auth.currentUserOrNull() == null) return emptyList()
            supabase.from("user_progress").select().decodeList<UserProgress>()
        } catch (e: Exception) {
            emptyList()
        }
    }

    // --- ЗАГРУЗКА ВОПРОСОВ (ИСПРАВЛЕННАЯ: ФИЛЬТРАЦИЯ В KOTLIN) ---

    suspend fun fetchQuestionsByTask(taskType: Int): List<ReadingQuestion> {
        return try {
            val currentUser = supabase.auth.currentUserOrNull()

            // 1. Получаем список ID решенных сетов
            val solvedSetIds: List<String> = if (currentUser != null) {
                val progress = supabase.from("user_progress").select {
                    filter {
                        eq("user_id", currentUser.id)
                        eq("task_id", taskType)
                    }
                }.decodeList<UserProgress>()
                progress.mapNotNull { it.setId }
            } else {
                emptyList()
            }

            // 2. Загружаем сеты (только по типу задачи)
            val allSets = supabase.from("reading_sets").select {
                filter {
                    eq("task_type", taskType)
                    // Убрали проблемный фильтр not.ilike отсюда
                }
            }.decodeList<ReadingSet>()

            // 3. ФИЛЬТРАЦИЯ В КОТЛИНЕ:
            // а) Исключаем те, где в названии есть "EXAMEN"
            // б) Ищем первый, которого нет в решенных (solvedSetIds)
            val targetSet = allSets
                .filter { !it.title.contains("EXAMEN", ignoreCase = true) } // <--- Фильтр здесь
                .firstOrNull { it.id !in solvedSetIds }

            if (targetSet == null) return emptyList()

            // 4. Загружаем вопросы для найденного сета
            supabase.from("reading_items").select {
                filter { eq("set_id", targetSet.id) }
                order("order_index", Order.ASCENDING)
            }.decodeList<ReadingQuestion>()

        } catch (e: Exception) {
            console.error("Error fetching questions: ${e.message}")
            emptyList()
        }
    }

    suspend fun saveAttempt(
        taskId: Int,
        setTitle: String,
        score: Int,
        total: Int,
        durationSeconds: Int,
        details: List<AttemptDetail>
    ) {
        val currentUser = supabase.auth.currentUserOrNull() ?: return

        val attempt = TaskAttempt(
            taskId = taskId,
            setTitle = setTitle,
            score = score,
            totalQuestions = total,
            durationSeconds = durationSeconds,
            details = details,
            userId = currentUser.id
        )

        try {
            supabase.from("task_attempts").insert(attempt)
        } catch (e: Exception) {
            console.error("Ошибка сохранения истории", e)
        }
    }

    // 2. Получение всей истории
    suspend fun getHistory(): List<TaskAttempt> {
        return try {
            val currentUser = supabase.auth.currentUserOrNull() ?: return emptyList()

            supabase.from("task_attempts").select {
                filter { eq("user_id", currentUser.id) }
                order("created_at", Order.DESCENDING) // Сначала новые
            }.decodeList<TaskAttempt>()
        } catch (e: Exception) {
            console.error("Ошибка загрузки истории", e)
            emptyList()
        }
    }

    // --- ЭКЗАМЕН (СБОРКА ВАРИАНТА 38 вопросов) ---
    // --- ЗАГРУЗКА ЭКЗАМЕНА (РЕЖИМ СИМУЛЯЦИИ) ---
    // --- ЭКЗАМЕН (СТРОГИЙ РЕЖИМ - БЕЗ ПОВТОРОВ) ---
    suspend fun fetchExamSet(): Map<Int, List<ReadingQuestion>> {
        return try {
            val currentUser = supabase.auth.currentUserOrNull() ?: return emptyMap()

            // 1. Загружаем историю (что уже решено)
            val progressList = supabase.from("user_progress").select {
                filter { eq("user_id", currentUser.id) }
            }.decodeList<UserProgress>()
            val solvedSetIds = progressList.mapNotNull { it.setId }.toSet()

            console.log(">>> Решенные сеты: $solvedSetIds") // Лог для проверки

            // 2. Загружаем ВСЕ экзаменационные сеты
            val allExamSets = supabase.from("reading_sets").select {
                filter { ilike("title", "%EXAMEN%") }
            }.decodeList<ReadingSet>()

            console.log(">>> Найдено всего экзаменационных сетов: ${allExamSets.size}")

            if (allExamSets.isEmpty()) return emptyMap()

            val setsByTask = allExamSets.groupBy { it.taskType }
            val resultMap = mutableMapOf<Int, List<ReadingQuestion>>()

            // 3. Собираем экзамен (СТРОГО НЕРЕШЕННЫЕ)

            // Функция: вернуть сет, если он еще не решен. Иначе вернуть null.
            fun getUnsolvedSet(taskType: Int): ReadingSet? {
                val available = setsByTask[taskType] ?: emptyList()
                return available.firstOrNull { it.id !in solvedSetIds }
            }

            // Tarea 1, 2, 3
            getUnsolvedSet(1)?.let { resultMap[1] = fetchQuestions(it.id) }
            getUnsolvedSet(2)?.let { resultMap[2] = fetchQuestions(it.id) }
            getUnsolvedSet(3)?.let { resultMap[3] = fetchQuestions(it.id) }

            // Tarea 4 (Нужны 2 части)
            val t4Sets = setsByTask[4] ?: emptyList()

            // Часть 1: Ищем первый нерешенный
            val t4Set1 = t4Sets.firstOrNull { it.id !in solvedSetIds }

            if (t4Set1 != null) {
                resultMap[4] = fetchQuestions(t4Set1.id)

                // Часть 2: Ищем второй нерешенный, который не равен первому
                val t4Set2 = t4Sets.firstOrNull { it.id !in solvedSetIds && it.id != t4Set1.id }
                if (t4Set2 != null) {
                    resultMap[5] = fetchQuestions(t4Set2.id)
                }
            }

            // Tarea 5
            getUnsolvedSet(5)?.let { resultMap[6] = fetchQuestions(it.id) }

            console.log(">>> Сформирован экзамен из ${resultMap.size} задач")
            return resultMap

        } catch (e: Exception) {
            console.error("Error exam: ${e.message}")
            emptyMap()
        }
    }

    private suspend fun fetchQuestions(setId: String): List<ReadingQuestion> {
        return supabase.from("reading_items").select {
            filter { eq("set_id", setId) }
            order("order_index", Order.ASCENDING)
        }.decodeList<ReadingQuestion>()
    }
    suspend fun getMyPracticeProgressOnly(): List<UserProgress> {
        return try {
            val currentUser = supabase.auth.currentUserOrNull() ?: return emptyList()

            // 1. Загружаем весь прогресс пользователя
            val allProgress = supabase.from("user_progress").select {
                filter { eq("user_id", currentUser.id) }
            }.decodeList<UserProgress>()

            // 2. ИСПРАВЛЕНИЕ: Загружаем сеты целиком (убираем columns = ...)
            // Чтобы сериализатор не ругался на отсутствие обязательных полей
            val examSets = supabase.from("reading_sets").select {
                filter { ilike("title", "%EXAMEN%") }
            }.decodeList<ReadingSet>()

            val examSetIds = examSets.map { it.id }.toSet()

            // 3. Возвращаем только тот прогресс, чей set_id НЕ входит в список экзаменов
            allProgress.filter { it.setId !in examSetIds }

        } catch (e: Exception) {
            console.error("Ошибка загрузки прогресса практики: ${e.message}")
            emptyList()
        }
    }
}
